'use client'

import { Button } from '@/core/components/form/button/button'
import { useEffect } from 'react'

import { cn } from '@/core/utils/common'

import RewardIcon from '@/features/veluvit/components/attendance/reward-icon'
import { SkewTag, SpeedLines } from '@/features/veluvit/components/ui/fx'
import { useCatalogMedia } from '@/features/veluvit/hooks/use-app-data'
import { useSessionStore } from '@/features/veluvit/stores/session.store'
import {
  FINAL_DAY,
  attendDays,
  attendReward,
} from '@/features/veluvit/lib/attendance'
import { fmt } from '@/features/veluvit/lib/format'
import { useUiStore } from '@/features/veluvit/stores/ui.store'

import type { CSSProperties } from 'react'

const HOLD_MS = 1900

/* 튀는 조각 10개의 방향 — 고정값이라 매 렌더 같은 궤적이다 */
const SPARKS = [
  { id: 'a', dx: -120, dy: -70 },
  { id: 'b', dx: 118, dy: -80 },
  { id: 'c', dx: -40, dy: -130 },
  { id: 'd', dx: 52, dy: -122 },
  { id: 'e', dx: -132, dy: 18 },
  { id: 'f', dx: 136, dy: 10 },
  { id: 'g', dx: -86, dy: 92 },
  { id: 'h', dx: 94, dy: 88 },
  { id: 'i', dx: -8, dy: 128 },
  { id: 'j', dx: 20, dy: -150 },
]

/**
 * 출석 보상 획득 연출 — 어느 화면에서 받든(출석 화면·PC 홈 레일·무료 젬) 앱 전체 위에 한 번 띄운다.
 * 보상이 커지며 튀어나오고, 뒤로 빛살이 돈다. 누르면 바로 닫히고 아니면 1.9초 뒤 사라진다.
 * 끝나면 justStamped 를 비워 출석판의 도장 애니메이션도 한 번만 돈다.
 */
export default function RewardBurstHost() {
  const { catalog } = useCatalogMedia()
  const rewards = useSessionStore((state) => state.data?.attendance.rewards)
  const stamped = useUiStore((s) => s.justStamped)

  useEffect(() => {
    if (stamped === null) return
    const timer = setTimeout(
      () => useUiStore.getState().set({ justStamped: null }),
      HOLD_MS
    )
    return () => clearTimeout(timer)
  }, [stamped])

  const day =
    stamped === null
      ? undefined
      : attendDays(rewards, catalog.attend).find((a) => a.d === stamped)
  if (!day) return null
  const reward = attendReward(day)
  // 마지막 날(최종 보상)은 금색 연출 — 예전 로얄 해금권 자리
  const isFinal = day.d === FINAL_DAY
  const amount =
    reward.kind === 'gem'
      ? `+${fmt(reward.amount)}`
      : `${reward.label} ${reward.amount}장`

  return (
    <div
      key={stamped}
      className="absolute inset-0 z-[300] flex animate-fade-in flex-col items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_50%_42%,color-mix(in_srgb,var(--point)_30%,transparent),rgb(0_0_0/0.94)_58%)] p-6 text-center backdrop-blur-[6px]"
    >
      {/* 만화 집중선 + 반짝이 — 가챠 획득 화면처럼 화면 전체가 반응한다 */}
      <SpeedLines />
      <p role="status" className="sr-only">
        {day.d}일차 출석 보상{' '}
        {reward.kind === 'gem' ? `젬 ${fmt(reward.amount)}개` : amount}를
        받았어요
      </p>
      <Button
        type="button"
        aria-label="보상 연출 닫기"
        onClick={() => useUiStore.getState().set({ justStamped: null })}
        className="absolute inset-0 cursor-pointer"
        variant="bare"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none relative grid size-60 place-items-center"
      >
        <span
          className={cn(
            'absolute inset-0 animate-rays rounded-full [mask-image:radial-gradient(circle,#000_30%,transparent_70%)]',
            isFinal
              ? 'bg-[repeating-conic-gradient(rgb(255_209_102/0.45)_0_9deg,transparent_9deg_24deg)]'
              : 'bg-[repeating-conic-gradient(color-mix(in_srgb,var(--point)_38%,transparent)_0_9deg,transparent_9deg_24deg)]'
          )}
        />
        {SPARKS.map((s) => (
          <i
            key={s.id}
            // 조각마다 날아갈 방향은 particle 키프레임이 CSS 변수로 읽는다
            style={
              { '--dx': `${s.dx}px`, '--dy': `${s.dy}px` } as CSSProperties
            }
            className={cn(
              'absolute top-1/2 left-1/2 size-2 animate-particle rounded-full [animation-delay:0.25s]',
              isFinal ? 'bg-gem' : 'bg-point'
            )}
          />
        ))}
        <span className="relative animate-reward-pop text-c96 leading-none drop-shadow-[0_12px_28px_rgb(0_0_0/0.55)]">
          <RewardIcon kind={reward.kind} art px={256} />
        </span>
      </span>
      <span
        aria-hidden="true"
        className="pointer-events-none -mt-4 animate-reward-pop"
      >
        <SkewTag tone={isFinal ? 'gold' : 'point'}>{`DAY ${day.d}`}</SkewTag>
      </span>
      <b
        aria-hidden="true"
        className={cn(
          'pointer-events-none mt-2 animate-reward-pop bg-clip-text text-c56 leading-[1.1] font-bold text-transparent drop-shadow-[0_6px_24px_rgb(0_0_0/0.6)] [animation-delay:0.12s]',
          isFinal || reward.kind === 'gem'
            ? 'bg-[linear-gradient(180deg,#fff3d1_10%,#f5c26b_55%,#d9973a)]'
            : 'bg-[linear-gradient(180deg,#FFFFFF_10%,#ff6a55)]'
        )}
      >
        {amount}
      </b>
      <span
        aria-hidden="true"
        className="pointer-events-none mt-5 text-c12 text-t3"
      >
        누르면 닫혀요
      </span>
    </div>
  )
}
